<script lang="ts" setup>
import {computed, nextTick, onMounted, onUnmounted, Ref, ref, watch} from "vue";

import useStore from "@src/stores/store";
import {useAuthStore} from "@src/stores/auth";
import ChatMessage from "@src/types/ChatMessage";
import {useChatSessionStore} from "@src/stores/chatSession";
import Message from "@src/components/views/HomeView/MainArea/ChatArea/Message/Message.vue";
import TimelineDivider from "@src/components/views/HomeView/MainArea/ChatArea/TimelineDivider.vue";
import {useMessageStore} from "@src/stores/messages";
import GroupMessage from "@src/components/views/HomeView/MainArea/ChatArea/Message/GroupMessage.vue";
import GroupMessageType from "@src/types/GroupMessage"
import {useContacts} from "@src/composables/useContacts";
import Contact from "@src/types/Contact";
import SessionService from "@src/services/session";
import Button from "@src/components/ui/inputs/Button.vue";
import {ChevronDownIcon} from "@heroicons/vue/24/outline";

// ─── Tipos auxiliares ──────────────────────────────────────────────────────────

interface HistoryMessage extends ChatMessage {
    session_id: string | number;
}

interface HistoryBlock {
    sessionId: string | number;
    sessionLabel?: string;   // ex: "Sessão encerrada em 12/03/2025 às 14:30"
    sessionEndedAt?: string; // ISO string
    sessionChannelName?: string;
    channelName?: string;
    messages: HistoryMessage[];
    forbidden?: boolean;     // true quando o usuário não tem permissão para ver os dados
}

// ─── Composables / stores ─────────────────────────────────────────────────────

const contactsComposable = useContacts()
const contacts = contactsComposable.getAllContacts()
let mutationObserver: MutationObserver | null = null;

const sessionStore = useChatSessionStore();
const store = useStore();
const userStore = useAuthStore();
const messageStore = useMessageStore()

// ─── Refs de UI ───────────────────────────────────────────────────────────────

const container: Ref<HTMLElement | null> = ref(null);

// ─── Estado do histórico retroativo ──────────────────────────────────────────

/**
 * Blocos de histórico já carregados (sessões antigas).
 * O índice 0 é o bloco mais ANTIGO carregado; o último é o mais RECENTE
 * antes da sessão atual.
 */
const historyBlocks = ref<HistoryBlock[]>([]);

/** Impede requisições duplicadas enquanto já está buscando */
const isLoadingHistory = ref(false);

/** Sinaliza que não há mais nada para carregar (todas as sessões esgotadas) */
const historyExhausted = ref(false);

/**
 * Cursor de paginação retroativa:
 * - `historySessionCursor` armazena o id da sessão que será buscada na próxima
 *   chamada (começa pela sessão "anterior" à atual).
 * - `historyPageCursor` é o número da página dentro dessa sessão.
 *   Usamos paginação reversa: página 1 = últimas 20 mensagens, página 2 = as 20
 *   anteriores a essas, etc.
 */
const historySessionCursor = ref<string | number | null>(null);
const historyPageCursor = ref(1);

// ─── Computed helpers ─────────────────────────────────────────────────────────

const isGroup = computed(() => sessionStore.type === 'group')

const isBot = computed(() => {
    const status = String((sessionStore.selectedSession as any)?.status ?? "");
    return ["bot", "gpt", "gemini", "paused"].includes(status);
})

const messages = computed(() => {
    if (isGroup.value) return messageStore.groupMessages;
    if (isBot.value) return messageStore.botMessages;
    return sessionStore.selectedSession?.messages ?? [];
})

const isSelf = (message: ChatMessage | GroupMessageType): boolean => {
    if (isGroup.value) {
        return Boolean(userStore.user && message.user_id && message.user_id === userStore.user.id);
    }
    return Boolean(userStore.user && message.from === 'user' || (message.from === 'system' && message.type !== 'warning'));
};

const hasContact = computed(() => {
    return contacts.value?.some((contact: Contact) => contact.costumer_id === sessionStore.selectedSession?.costumer?.id)
})
const contact = computed(() => {
    return contacts.value?.find((contact: Contact) => contact.costumer_id === sessionStore.selectedSession?.costumer?.id)
})

// ─── Divisores por dia ────────────────────────────────────────────────────────

const firstMessageIndices = computed(() => {
    const messagesByDay = new Map()
    const indices = new Set<number>()

    messages.value?.forEach((message, index) => {
        if (message.created_at) {
            const date = new Date(message.created_at)
            date.setHours(0, 0, 0, 0)
            const dayKey = date.toISOString()

            if (!messagesByDay.has(dayKey)) {
                messagesByDay.set(dayKey, message)
                indices.add(index)
            }
        }
    })

    return indices
})

const renderDivider = (index: number): boolean => firstMessageIndices.value.has(index);

/** Mesmo cálculo, mas por bloco de histórico */
const firstMessageIndicesForBlock = (block: HistoryBlock): Set<number> => {
    const messagesByDay = new Map()
    const indices = new Set<number>()

    block.messages.forEach((message, index) => {
        if (message.created_at) {
            const date = new Date(message.created_at)
            date.setHours(0, 0, 0, 0)
            const dayKey = date.toISOString()
            if (!messagesByDay.has(dayKey)) {
                messagesByDay.set(dayKey, message)
                indices.add(index)
            }
        }
    })

    return indices
}

// ─── Scroll helpers ───────────────────────────────────────────────────────────

const scrollToBottom = (behavior: ScrollBehavior = 'instant') => {
    const el = container.value;
    if (!el) return;
    el.scrollTo({top: el.scrollHeight, behavior: behavior});
};

const scrollAfterImagesLoad = () => {
    const el = container.value;
    if (!el || sessionStore.isUserScrolling) return;

    const images = Array.from(el.querySelectorAll<HTMLImageElement>('img'));
    const pending = images.filter(img => !img.complete);

    if (pending.length === 0) {
        scrollToBottom();
        return;
    }

    let loaded = 0;
    const onLoad = () => {
        loaded++;
        if (loaded >= pending.length) scrollToBottom();
    };
    pending.forEach(img => {
        img.addEventListener('load', onLoad, {once: true});
        img.addEventListener('error', onLoad, {once: true});
    });
};

// ─── Carregamento retroativo ──────────────────────────────────────────────────

/**
 * Contrato esperado:
 *   - Recebe: sessionId e page (página reversa, 1 = mais recentes)
 *   - Retorna: { messages: HistoryMessage[], hasMore: boolean,
 *               sessionLabel?: string, sessionEndedAt?: string,
 *               previousSessionId?: string | number | null,
 *               forbidden?: boolean }
 *
 * `previousSessionId` deve ser o id da sessão imediatamente anterior a
 * `sessionId` no histórico deste contato/grupo, ou null se não existir.
 *
 * `forbidden` é true quando o usuário autenticado não tem permissão para
 * visualizar o conteúdo da sessão. Nesse caso `messages` virá vazio e o
 * front deve exibir apenas um aviso de acesso restrito no lugar das mensagens.
 */
const fetchHistoryPage = async (
    sessionId: string | number,
    page: number
): Promise<{
    messages: HistoryMessage[];
    hasMore: boolean;
    sessionLabel?: string;
    sessionEndedAt?: string;
    channelName?: string;
    previousSessionId?: string | number | null;
    forbidden?: boolean;
}> => {
    return SessionService.fetchHistoryPage(sessionId, page);
}

/**
 * Formata o label do divisor de sessão.
 * Personalize conforme o formato de data do seu projeto.
 */
const formatSessionLabel = (block: HistoryBlock): string => {
    if (block.sessionLabel) return block.sessionLabel;
    if (block.sessionEndedAt) {
        const d = new Date(block.sessionEndedAt);
        return `Sessão encerrada em ${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        })}`;
    }
    return `Sessão anterior (Protocolo: ${block.sessionId}) - (Canal: ${block.sessionChannelName})`;
}

/**
 * Chama loadMoreHistory em loop até que o container tenha scroll disponível
 * ou não haja mais histórico para carregar.
 */
const fillScrollableHeight = async () => {
    const el = container.value;
    if (!el) return;

    while (!historyExhausted.value) {
        const hasScroll = el.scrollHeight > el.clientHeight + SCROLL_TOP_THRESHOLD;
        if (hasScroll) {
            break
        }
        if (isLoadingHistory.value) {
            await nextTick();
            continue;
        }
        await loadMoreHistory();
        await nextTick();
    }
};

/**
 * Disparado quando o usuário chega perto do topo.
 * Preserva a posição do scroll após inserir novos elementos acima.
 */
const loadMoreHistory = async () => {
    if (isLoadingHistory.value || historyExhausted.value) return;

    const el = container.value;
    if (!el) return;

    // Inicializa o cursor com a sessão anterior à atual (na primeira chamada)
    if (historySessionCursor.value === null) {
        const prevId = (sessionStore.selectedSession as any)?.previous_session_id ?? null;
        if (!prevId) {
            historyExhausted.value = true;
            return;
        }
        historySessionCursor.value = prevId;
        historyPageCursor.value = 1;
    }

    isLoadingHistory.value = true;

    // Salva a âncora de scroll ANTES de inserir conteúdo acima
    const previousScrollHeight = el.scrollHeight;

    try {
        const result = await fetchHistoryPage(historySessionCursor.value!, historyPageCursor.value);

        if (result.forbidden) {
            // Usuário sem permissão: registra bloco proibido e avança o cursor
            historyBlocks.value.unshift({
                sessionId: historySessionCursor.value!,
                sessionLabel: result.sessionLabel,
                sessionEndedAt: result.sessionEndedAt,
                messages: [],
                forbidden: true,
                sessionChannelName: result.channelName
            });

            if (result.previousSessionId) {
                historySessionCursor.value = result.previousSessionId;
                historyPageCursor.value = 1;
            } else {
                historyExhausted.value = true;
            }
            return;
        }

        if (!result.messages.length && !result.hasMore) {
            // Sessão atual esgotada; tenta a anterior
            if (result.previousSessionId) {
                historySessionCursor.value = result.previousSessionId;
                historyPageCursor.value = 1;
            } else {
                historyExhausted.value = true;
            }
            return;
        }

        // Mensagens vêm em ordem DESC da API → invertemos para exibir ASC
        const orderedMessages = [...result.messages].reverse();

        if (historyPageCursor.value === 1) {
            // Primeiro lote dessa sessão: cria um novo bloco no início
            historyBlocks.value.unshift({
                sessionId: historySessionCursor.value!,
                sessionLabel: result.sessionLabel,
                sessionEndedAt: result.sessionEndedAt,
                messages: orderedMessages,
                sessionChannelName: result.channelName
            });
        } else {
            // Lotes subsequentes da mesma sessão: prepend no bloco existente
            const block = historyBlocks.value[0];
            if (block && block.sessionId === historySessionCursor.value) {
                block.messages = [...orderedMessages, ...block.messages];
            }
        }

        if (!result.hasMore) {
            // Sessão esgotada: avança para a anterior
            if (result.previousSessionId) {
                historySessionCursor.value = result.previousSessionId;
                historyPageCursor.value = 1;
            } else {
                historyExhausted.value = true;
            }
        } else {
            historyPageCursor.value++;
        }

        // Restaura a posição do scroll para o usuário não "pular"
        await nextTick();
        const addedHeight = el.scrollHeight - previousScrollHeight;
        el.scrollTop += addedHeight;

    } finally {
        isLoadingHistory.value = false;
    }
};

// ─── Evento de scroll ─────────────────────────────────────────────────────────

const SCROLL_BOTTOM_THRESHOLD = 100; // px do fundo para considerar "no fundo"
const SCROLL_TOP_THRESHOLD = 120;    // px do topo para disparar load retroativo

const onScroll = () => {
    const el = container.value;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    sessionStore.isUserScrolling = distanceFromBottom > SCROLL_BOTTOM_THRESHOLD;

    // Carrega histórico ao chegar perto do topo
    if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
        loadMoreHistory();
    }
};

// ─── MutationObserver ─────────────────────────────────────────────────────────

const setupObserver = () => {
    mutationObserver?.disconnect();
    if (!container.value) return;

    mutationObserver = new MutationObserver(() => {
        if (!sessionStore.scrollReady) return;
        if (sessionStore.isUserScrolling) return;
        scrollAfterImagesLoad();
    });

    mutationObserver.observe(container.value, {
        childList: true,
        subtree: true,
        characterData: true,
    });
};

// ─── Reset ao trocar de sessão ────────────────────────────────────────────────

watch(() => sessionStore.selectedSession?.id, async (newId, oldId) => {
    if (newId !== oldId) {
        // Reseta o estado do histórico retroativo
        historyBlocks.value = [];
        historyExhausted.value = false;
        historySessionCursor.value = null;
        historyPageCursor.value = 1;
        await nextTick();
        await fillScrollableHeight();
    }
    await nextTick();
    setupObserver();
});

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
    if (container.value) {
        sessionStore.setChatContainer(container.value);
        container.value.addEventListener('scroll', onScroll);
        setupObserver();
        await nextTick();
        await fillScrollableHeight();
        scrollToBottom();
    }
});

onUnmounted(() => {
    mutationObserver?.disconnect();
    container.value?.removeEventListener('scroll', onScroll);
});
</script>

<template>
    <div class="relative grow flex flex-col overflow-hidden">

        <!-- Loading overlay (sessão atual sendo carregada) -->
        <Transition name="fade">
            <div
                v-if="sessionStore.isLoadingSession"
                class="absolute inset-0 z-10 flex flex-col gap-3 px-5 py-5 overflow-hidden bg-white dark:bg-gray-900"
            >
                <div
                    v-for="i in 6"
                    :key="i"
                    :class="[
                        'flex gap-2 items-end',
                        i % 2 === 0 ? 'flex-row-reverse' : 'flex-row'
                    ]"
                >
                    <div class="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse shrink-0"/>
                    <div
                        :class="[
                            'h-10 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse',
                            i % 3 === 0 ? 'w-48' : i % 3 === 1 ? 'w-64' : 'w-36'
                        ]"
                    />
                </div>
            </div>
        </Transition>

        <!-- Área de scroll principal -->
        <div
            ref="container"
            class="grow px-5 py-5 flex flex-col overflow-y-scroll overflow-x-hidden"
        >

            <!-- ── Indicador de "sem mais histórico" ───────────────────── -->
            <div
                v-if="historyExhausted && historyBlocks.length > 0"
                class="flex items-center justify-center py-3 mb-2"
            >
                <span class="text-xs text-gray-400 dark:text-gray-500 italic">
                    Início do histórico
                </span>
            </div>

            <!-- ── Spinner de carregamento de histórico ───────────────── -->
            <Transition name="fade">
                <div
                    v-if="isLoadingHistory"
                    class="flex items-center justify-center py-4"
                >
                    <svg
                        class="animate-spin h-5 w-5 text-gray-400 dark:text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" d="M4 12a8 8 0 018-8v8H4z" fill="currentColor"/>
                    </svg>
                </div>
            </Transition>

            <!-- ── Blocos de histórico (sessões antigas) ───────────────── -->
            <template v-for="(block, blockIndex) in historyBlocks" :key="`block-${block.sessionId}`">

                <!-- Divisor de sessão -->
                <div class="flex items-center gap-3 my-4">
                    <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700"/>
                    <span class="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap px-1 select-none">
                        {{ formatSessionLabel(block) }}
                    </span>
                    <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700"/>
                </div>

                <!-- Acesso restrito -->
                <div
                    v-if="block.forbidden"
                    class="flex items-center justify-center py-3"
                >
                    <span class="text-xs text-gray-400 dark:text-gray-500 italic select-none">
                        Você não tem permissão para visualizar este atendimento.
                    </span>
                </div>

                <!-- Mensagens do bloco histórico -->
                <template v-else>
                    <div
                        v-for="(message, index) in block.messages"
                        :key="`hist-${block.sessionId}-${message.id}`"
                    >
                        <TimelineDivider
                            v-if="firstMessageIndicesForBlock(block).has(index)"
                            :message="message"
                        />

                        <Message
                            v-if="sessionStore.selectedSession && !isGroup"
                            :contact="hasContact ? contact : undefined"
                            :divider="firstMessageIndicesForBlock(block).has(index)"
                            :follow-up="false"
                            :message="message"
                            :self="isSelf(message)"
                            :session="sessionStore.selectedSession"
                        />

                        <GroupMessage
                            v-else-if="sessionStore.selectedSession"
                            :contact="hasContact ? contact : undefined"
                            :divider="firstMessageIndicesForBlock(block).has(index)"
                            :follow-up="false"
                            :message="(message as any)"
                            :self="isSelf(message)"
                            :session="(sessionStore.selectedSession as any)"
                        />
                    </div>
                </template>

            </template>

            <!-- ── Divisor "Sessão atual" (só aparece quando há histórico carregado) -->
            <div
                v-if="historyBlocks.length > 0"
                class="flex items-center gap-3 my-4"
            >
                <div class="flex-1 h-px bg-indigo-200 dark:bg-indigo-800"/>
                <span
                    class="text-xs font-medium text-indigo-500 dark:text-indigo-400 whitespace-nowrap px-1 select-none">
                    Sessão atual
                </span>
                <div class="flex-1 h-px bg-indigo-200 dark:bg-indigo-800"/>
            </div>

            <!-- ── Mensagens da sessão atual (lógica original) ─────────── -->
            <div
                v-for="(message, index) in messages"
                v-if="store.status !== 'loading' && !isGroup"
                :key="message.id"
            >
                <TimelineDivider
                    v-if="renderDivider(index)"
                    :message="message"
                />

                <Message
                    v-if="sessionStore.selectedSession"
                    :contact="hasContact ? contact : undefined"
                    :divider="renderDivider(index)"
                    :follow-up="false"
                    :message="(message as any)"
                    :self="isSelf(message)"
                    :session="sessionStore.selectedSession"
                />
            </div>

            <div
                v-for="(message, index) in messages"
                v-else-if="store.status !== 'loading'"
                :key="message.id"
            >
                <TimelineDivider
                    v-if="renderDivider(index)"
                    :message="message"
                />

                <GroupMessage
                    v-if="sessionStore.selectedSession"
                    :contact="hasContact ? contact : undefined"
                    :divider="renderDivider(index)"
                    :follow-up="false"
                    :message="(message as any)"
                    :self="isSelf(message)"
                    :session="(sessionStore.selectedSession as any)"
                />
            </div>

            <div
                v-if="sessionStore.isUserScrolling"
                class="h-auto w-full flex flex-row pr-1 sticky bottom-2 justify-end text-center ">
                <Button
                    class="bg-gray-500 text-white p-4 text-sm rounded-full opacity-50 hover:opacity-75 cursor-pointer hover:bg-gray-800 dark:bg-gray-300 dark:hover:bg-gray-400"
                    @click="scrollToBottom('smooth')">
                    <ChevronDownIcon class="w-6 h-6"/>
                </Button>
            </div>

        </div>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
